// FILE: src/index.js
import '@fortawesome/fontawesome-free/css/all.min.css';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';

// Universal: when a form button is clicked, blur any focused input (helps iOS drop zoom)
(function attachSubmitBlurShim() {
  if (typeof window === "undefined") return;
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target;
      if (!(el instanceof Element)) return;
      const isAction =
        el.closest('button[type="submit"], input[type="submit"], .btn-main, .btn-danger, .btn-cancel, .btn');
      if (!isAction) return;

      // blur focused input
      const a = document.activeElement;
      if (a && a instanceof HTMLElement) a.blur();

      // tiny viewport jiggle for stubborn iOS zoom states (no-op elsewhere)
      const vp = document.querySelector('meta[name="viewport"]');
      if (vp) {
        const base = vp.getAttribute("content") || "width=device-width, initial-scale=1, viewport-fit=cover";
        if (!/maximum-scale=/.test(base)) {
          vp.setAttribute("content", base + ", maximum-scale=1");
          setTimeout(() => vp.setAttribute("content", base), 120);
        }
      }
    },
    true
  );
})();

// Keep CSS var --header-height in sync with the actual header height
(function syncHeaderHeight() {
  if (typeof window === "undefined") return;
  function update() {
    const header = document.querySelector(".app-header");
    const px = header ? Math.round(header.getBoundingClientRect().height) : 56;
    document.documentElement.style.setProperty("--header-height", px + "px");
  }
  window.addEventListener("load", update, { once: true });
  window.addEventListener("resize", update);
  const mo = new MutationObserver(update);
  mo.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["style", "class"]
  });
  update();
})();



// WHY: Keep Firebase Auth sessions consistent per-origin,
// but allow local dev and Firebase *preview* channels.
const CANONICAL_HOST = 'lastwishbox-business.web.app';

(function enforceCanonicalDomain() {
  if (typeof window === 'undefined') return;

  // Preview builds set this env var in the preview workflow.
  // When present, we disable the redirect entirely.
  if (process.env.REACT_APP_DISABLE_CANONICAL === '1') return;

  const h = window.location.hostname; // hostname is cleaner than host (no port)
  const { pathname, search, hash } = window.location;

  const isLocal =
    h === 'localhost' || h === '127.0.0.1' || /\.local$/.test(h);

  // Firebase preview channels look like "<site>--<channel>-<id>.web.app"
  const isPreview =
    h.includes('--') && (h.endsWith('.web.app') || h.endsWith('.firebaseapp.com'));

  // Default Firebase hosts (not custom domain)
  const isDefaultFirebase =
    h.endsWith('.web.app') || h.endsWith('.firebaseapp.com');

  // Only redirect when we're on default Firebase hosts (not preview),
  // not local, and not already on the canonical host.
  if (!isLocal && !isPreview && isDefaultFirebase && h !== CANONICAL_HOST) {
    const target = `https://${CANONICAL_HOST}${pathname}${search}${hash}`;
    try {
      // Use replace() so "wrong host" isn't left in browser history
      window.location.replace(target);
    } catch {
      window.location.href = target;
    }
  }
})();

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
